// ============================================
// Analytics Types
// Frontend interfaces for analytics API responses
// ============================================

// ============================================
// Shared Enums
// ============================================

/**
 * Period type for data aggregation and forecasting
 */
export enum PeriodType {
  DAILY = 'daily',
  WEEKLY = 'weekly',
  MONTHLY = 'monthly',
  YEARLY = 'yearly',
  CUSTOM = 'custom',
}

/**
 * Trend direction indicator
 */
export enum TrendDirection {
  UPWARD = 'upward',
  DOWNWARD = 'downward',
  STABLE = 'stable',
}

/**
 * Velocity classification for demand planning
 */
export enum VelocityClassification {
  FAST = 'fast',
  MEDIUM = 'medium',
  SLOW = 'slow',
  STAGNANT = 'stagnant',
}

/**
 * Priority levels for restock recommendations
 */
export enum RestockPriority {
  HIGH = 'high',
  MEDIUM = 'medium',
  LOW = 'low',
  NONE = 'none',
}

// ============================================
// Forecast Types
// ============================================

/**
 * Request parameters for sales forecast API
 */
export interface GetSalesForecastParams {
  organizationId: string;
  forecastPeriods?: number;
  periodType?: PeriodType;
  startDate?: string;
  endDate?: string;
  locationId?: string;
}

/**
 * Request parameters for seasonal trends API
 */
export interface GetSeasonalTrendsParams {
  organizationId: string;
  periodType?: PeriodType;
  startDate?: string;
  endDate?: string;
  locationId?: string;
}

/**
 * Individual forecast data point with confidence interval
 */
export interface ForecastDataPoint {
  /** Date string formatted by period type */
  period: string;
  /** Predicted sales value */
  predictedValue: number;
  /** Lower confidence interval bound */
  lowerBound: number;
  /** Upper confidence interval bound */
  upperBound: number;
  /** Confidence level (0-1) */
  confidence: number;
}

/**
 * Seasonal pattern data for a specific period
 */
export interface SeasonalTrendData {
  /** Period identifier (e.g., "January", "Q1", "Week 1") */
  period: string;
  /** Average sales for this period */
  averageValue: number;
  /** Number of sales in this period */
  salesCount: number;
  /** Percentage of total sales */
  percentageOfTotal: number;
  /** Percentage change from previous year */
  yearOverYearChange: number;
}

/**
 * Complete sales forecast response
 */
export interface SalesForecastResponse {
  organizationId: string;
  forecastPeriod: {
    startDate: string;
    endDate: string;
    type: PeriodType;
    periods: number;
  };
  historicalPeriod: {
    startDate: string;
    endDate: string;
    dataPoints: number;
  };
  trendDirection: TrendDirection;
  /** Trend strength (0-1), how strong the trend is */
  trendStrength: number;
  averagePredictedValue: number;
  totalPredictedValue: number;
  forecastData: ForecastDataPoint[];
}

/**
 * Seasonal trends analysis response
 */
export interface SeasonalTrendsResponse {
  organizationId: string;
  analysisPeriod: {
    startDate: string;
    endDate: string;
    type: PeriodType;
  };
  overallTrend: TrendDirection;
  seasonalPatterns: SeasonalTrendData[];
  peakPeriod: {
    period: string;
    value: number;
  };
  lowPeriod: {
    period: string;
    value: number;
  };
  /** Seasonality strength (0-1), how strong the seasonal pattern is */
  seasonalityStrength: number;
}

// ============================================
// Demand Planning Types
// ============================================

/**
 * Request parameters for demand planning API
 */
export interface DemandRequestParams {
  organizationId?: string;
  productId?: string;
  locationId?: string;
  velocityType?: VelocityClassification;
  startDate?: string;
  endDate?: string;
  forecastDays?: number;
  limit?: number;
}

/**
 * Product demand data with restock recommendations
 */
export interface ProductDemandData {
  productId: string;
  productName: string;
  sku: string;
  currentStock: number;
  velocityClassification: VelocityClassification;
  averageDailyDemand: number;
  forecastedDemand: number;
  daysUntilStockout: number | null;
  restockPriority: RestockPriority;
  recommendedOrderQuantity: number;
}

/**
 * Demand forecast response for a specific product
 */
export interface DemandForecastResponse {
  period: {
    startDate: string;
    endDate: string;
    forecastDays: number;
  };
  product: {
    productId: string;
    productName: string;
    currentStock: number;
    velocityClassification: VelocityClassification;
  };
  forecast: {
    averageDailyDemand: number;
    forecastedDemand: number;
    confidenceInterval: {
      lower: number;
      upper: number;
    };
    daysUntilStockout: number | null;
  };
  recommendation: {
    restockPriority: RestockPriority;
    recommendedOrderQuantity: number;
    suggestedRestockDate: string | null;
  };
}

/**
 * Restock recommendations response with priority summary
 */
export interface RestockRecommendationsResponse {
  period: {
    startDate: string;
    endDate: string;
    analyzedAt: string;
  };
  summary: {
    totalProducts: number;
    highPriority: number;
    mediumPriority: number;
    lowPriority: number;
  };
  recommendations: ProductDemandData[];
  velocityDistribution: {
    fast: number;
    medium: number;
    slow: number;
    stagnant: number;
  };
}

/**
 * Complete demand planning analysis response
 */
export interface DemandPlanningAnalysisResponse {
  organizationId: string;
  locationId?: string;
  period: {
    startDate: string;
    endDate: string;
    forecastDays: number;
  };
  forecast?: DemandForecastResponse;
  recommendations?: RestockRecommendationsResponse;
  velocityAnalysis: {
    classification: VelocityClassification;
    averageDailyDemand: number;
    trendDirection: 'increasing' | 'decreasing' | 'stable';
  };
}

// ============================================
// Profit Analysis Types
// ============================================

/**
 * Request parameters for profit analysis API
 */
export interface ProfitRequestParams {
  organizationId: string;
  locationId?: string;
  periodType?: PeriodType;
  startDate?: string;
  endDate?: string;
  limit?: number;
}

/**
 * Request parameters for top profitable products API
 */
export interface TopProfitableProductsRequestParams {
  organizationId: string;
  locationId?: string;
  startDate?: string;
  endDate?: string;
  limit?: number;
}

/**
 * Profit data for a specific period
 */
export interface PeriodProfitData {
  /** Date string formatted by period type */
  period: string;
  revenue: number;
  /** Cost of Goods Sold */
  cogs: number;
  profit: number;
  marginPercent: number;
  salesCount: number;
}

/**
 * Profit data for a specific product
 */
export interface ProductProfitData {
  productId: string;
  productName: string;
  quantitySold: number;
  revenue: number;
  totalCost: number;
  profit: number;
  marginPercent: number;
}

/**
 * Complete profit analysis response with summary and detailed breakdowns
 */
export interface ProfitAnalysisResponse {
  organizationId: string;
  period: {
    startDate: string;
    endDate: string;
    type: PeriodType;
  };
  summary: {
    /** Total sales revenue (sum of subtotals) */
    revenue: number;
    /** Cost of Goods Sold (sum of totalCost) */
    cogs: number;
    /** Gross profit: revenue - cogs */
    grossProfit: number;
    /** Gross margin percent: (grossProfit / revenue) * 100 */
    grossMarginPercent: number;
    salesCount: number;
    itemsSold: number;
    averageMarginPerSale: number;
  };
  byPeriod: PeriodProfitData[];
  topProfitableProducts: ProductProfitData[];
  lowMarginProducts: ProductProfitData[];
}

/**
 * Profit data grouped by period response
 */
export interface ProfitByPeriodResponse {
  organizationId: string;
  period: {
    startDate: string;
    endDate: string;
    type: PeriodType;
  };
  data: PeriodProfitData[];
}

/**
 * Top profitable products response
 */
export interface TopProfitProductsResponse {
  organizationId: string;
  period: {
    startDate: string;
    endDate: string;
  };
  products: ProductProfitData[];
}

// ============================================
// Helper Labels
// ============================================

export const PeriodTypeLabels: Record<PeriodType, string> = {
  [PeriodType.DAILY]: 'Daily',
  [PeriodType.WEEKLY]: 'Weekly',
  [PeriodType.MONTHLY]: 'Monthly',
  [PeriodType.YEARLY]: 'Yearly',
  [PeriodType.CUSTOM]: 'Custom',
};

export const TrendDirectionLabels: Record<TrendDirection, string> = {
  [TrendDirection.UPWARD]: 'Upward',
  [TrendDirection.DOWNWARD]: 'Downward',
  [TrendDirection.STABLE]: 'Stable',
};

export const VelocityClassificationLabels: Record<VelocityClassification, string> = {
  [VelocityClassification.FAST]: 'Fast Moving',
  [VelocityClassification.MEDIUM]: 'Medium Moving',
  [VelocityClassification.SLOW]: 'Slow Moving',
  [VelocityClassification.STAGNANT]: 'Stagnant',
};

export const RestockPriorityLabels: Record<RestockPriority, string> = {
  [RestockPriority.HIGH]: 'High Priority',
  [RestockPriority.MEDIUM]: 'Medium Priority',
  [RestockPriority.LOW]: 'Low Priority',
  [RestockPriority.NONE]: 'No Restock Needed',
};
